<template>
  <div class="account-setting">
    <div class="flex items-start">
      <div class="flex flex-col items-center">
        <Avatar :size="120" :url="previewFile(accountInfo.avatar)" />
        <a-button @click="selectAvatar" type="text" class="mt-4 mx-auto text-center">编辑</a-button>
      </div>
      <div class="flex-1 pl-[32px]">
        <SingleEditPForm
          ref="formRef"
          class="tow-line-form"
          :form-items="basicFormItems"
          :form-data="accountInfo"
          @change="handleChange"
        >
          <template #bottomname>
            <a-button @click="formRef.editByKey('name')" type="link">修改</a-button>
          </template>
          <template #bottomgender>
            <a-button @click="formRef.editByKey('gender')" type="link">修改</a-button>
          </template>
          <template #bottomuserName>
            <a-button @click="openAuth(userHasAccount ? 'pwd' : 'account')" type="link">
              {{ userHasAccount ? '修改密码' : '设置' }}
            </a-button>
          </template>
          <template #bottomemail>
            <a-button @click="openAuth('email')" type="link">修改</a-button>
          </template>
          <template #bottomphone>
            <a-button @click="openAuth('phone')" type="link">修改</a-button>
          </template>
        </SingleEditPForm>
        <UpdateSecurityModal
          ref="updateSecurityModalRef"
          show-wechat-form
          v-model:visible="authenticationInfo.visible"
          :type="authenticationInfo.type"
          @wechat-change="queryBasicInfo"
          @refresh="queryBasicInfo"
          @wechat-bind-request="handleWechatBindRequest"
        />
      </div>
    </div>
  </div>
  <AuthenticationModal
    v-model:visible="authenticationInfo.verificationVisible"
    :user="accountInfo"
    @success="handleSuccess"
  ></AuthenticationModal>
</template>

<script setup lang="ts">
import {
  querySelfInfo,
  updatePhoneInfo,
  updatePassword,
  updateNameInfo,
  updateGenderInfo,
  updateAvatar,
  checkCode,
  updateEmployeeName,
  updateCart,
  queryUserEmployeeInfo,
  queryLoginInfo,
  settingUserInfo,
  verificationCode,
} from '@elecmap/api/src/ram'
import { notificationError } from '@elecmap/shared/src/utils/notification'
import { onMounted, reactive, ref } from 'vue'
import { Avatar } from '@elecmap/elecmap-ui'
import { previewFile } from '@elecmap/api/src/cad/file'
import { selectFile } from '@elecmap/shared/src/utils/dom'
import useUserStore from '@/store/user'
import SingleEditPForm from '@elecmap/shared/src/components/form/SingleEditPForm.vue'
import type { PFormItemProps } from '@elecmap/shared/src/components/form/SingleEditPForm.vue'
import { EditOutlined } from '@ant-design/icons-vue'
import AuthenticationModal from './authentication-modal.vue'
import UpdateSecurityModal from './update-security-modal.vue'

const userStore = useUserStore()

const authenticationInfo = reactive({
  verificationVisible: false,
  visible: false,
  type: '',
})
const accountInfo = reactive({
  avatar: '',
  name: '',
  organizationRemarkName: '',
  enrollTime: '',
  userName: '',
  email: '',
  phone: '',
  phoneMasked: '',
  newPhone: '',
  bindWechat: false,
  weChat: '',
  gender: '',
  genderName: '',
  code: '',
  card: '',
  pwdShow: '* * * * * * * * * *',
  wechatNickname: '',
} as any)
const weChatStatus = ref(true)
const userHasAccount = ref(false)
const formRef = ref()
const updateSecurityModalRef = ref<{ openWeChatBinding: () => void } | null>(null)
const maskPhone = (phone?: string) => {
  if (!phone) return ''
  return phone.replace(/^(\d{3})\d{4}(\d+)$/, '$1****$2')
}
const queryBasicInfo = () => {
  querySelfInfo().then((data: any) => {
    accountInfo.name = data.name
    accountInfo.card = data.card
    accountInfo.enrollTime = data.registerTime
    accountInfo.userName = data.userName
    accountInfo.userName1 = data.userName
    userHasAccount.value = !!data.userName
    accountInfo.email = data.email
    accountInfo.phone = data.phone
    accountInfo.phoneMasked = maskPhone(data.phone)
    accountInfo.gender = data.gender
    accountInfo.genderName = data.genderName
    accountInfo.avatar = data.avatar
    accountInfo.bindWechat = data.bindWechat
    accountInfo.weChat = data.weChat || data.wechatNickname || ''
    accountInfo.wechatNickname = accountInfo.weChat
    weChatStatus.value = data.bindWechat
    const bindWechatErrorMessages = data.bindWechatErrorMessage
    if (bindWechatErrorMessages) {
      notificationError(bindWechatErrorMessages)
    }
  })
}

async function selectAvatar() {
  const f = await selectFile()
  await updateAvatar(f[0].uuid)
  accountInfo.avatar = f[0].uuid
  userStore.userInfo.avatar = accountInfo.avatar
}

async function handleChange(key: string, value: any) {
  console.log('handle change: ', Date.now())
  switch (key) {
    case 'name':
      await updateNameInfo({ name: value })
      accountInfo.name = value
      userStore.userInfo.name = value
      break
    case 'gender':
      accountInfo.gender = value
      accountInfo.genderName = value === 'man' ? '男' : '女'
      await updateGenderInfo({ gender: value })
      userStore.userInfo.gender = value
      userStore.userInfo.genderName = accountInfo.genderName
      break
  }
}

function openAuth(type: string) {
  authenticationInfo.verificationVisible = true
  // authenticationInfo.visible = true
  authenticationInfo.type = type
}

function handleWechatBindRequest() {
  authenticationInfo.type = 'wechatBind'
  authenticationInfo.verificationVisible = true
}

function handleSuccess() {
  authenticationInfo.verificationVisible = false
  if (authenticationInfo.type === 'wechatBind') {
    authenticationInfo.type = ''
    updateSecurityModalRef.value?.openWeChatBinding()
    return
  }
  authenticationInfo.visible = true
}

onMounted(() => {
  queryBasicInfo()
  queryUserEmployeeInfo().then((data: any) => {
    accountInfo.organizationRemarkName = data.employeeName
  })
})

const basicFormItems: PFormItemProps[] = [
  {
    key: 'name',
    label: '姓名',
    type: 'Input',
  },
  {
    key: 'gender',
    label: '性别',
    type: 'Select',
    options: [
      { id: 'man', name: '男' },
      { id: 'female', name: '女' },
    ],
    displayKey: 'genderName',
  },
  {
    key: 'userName',
    label: '账号',
    type: 'Text',
    displayKey: 'userName1'
  },
  {
    key: 'email',
    label: '邮箱',
    type: 'Text',
  },
  {
    key: 'phone',
    label: '手机',
    type: 'Text',
    displayKey: 'phoneMasked',
  },
]
</script>

<style lang="less" scoped>
.account-setting {
  // padding: 0 32px;

  :deep(.em-user-avatar) {
    border-radius: 50%;
    overflow: hidden;
  }

  .tow-line-form {
    :deep(.ant-form-item) {
      flex-direction: column;
      margin-bottom: 12px;
      .ant-form-item-label {
        width: 100%;
        max-width: 100%;
        flex: none;
        display: block;
        margin-bottom: 12px;
        > label {
          font-size: 14px;
          font-weight: 450;
        }
      }
      .ant-form-item-control {
        flex: none;
      }
      .single-edit-trigger {
        width: auto;
        min-width: 30px;
      }
    }
    :deep(.form-item-bottom) {
      transform: translate(-6px, -12px);
    }
  }
}
</style>
